const LIGHTBOX_BODY_CLASS = 'product-gallery-lightbox-open'
const LIGHTBOX_FADE_MEDIA_QUERY = '(min-width: 990px)'
const LIGHTBOX_SLIDE_ACTIVE_CLASS = 'is-lightbox-slide-active'
const LIGHTBOX_FADE_READY_CLASS = 'is-lightbox-fade-ready'
const LIGHTBOX_ZOOM_CLASS = 'is-lightbox-zoomed'
const LIGHTBOX_ZOOM_DRAG_CLASS = 'is-lightbox-zooming-drag'
const LIGHTBOX_ZOOM_SCALE = 2.5
const LIGHTBOX_ZOOM_DRAG_THRESHOLD = 6

function isProductGalleryLightbox(node) {
  return node instanceof HTMLElement && node.classList.contains('js-product-lightbox')
}

function mountLightboxInBody(dialog) {
  if (!dialog || dialog.parentElement === document.body) return
  document.body.appendChild(dialog)
}

function mountAllLightboxes() {
  for (const dialog of document.querySelectorAll('.js-product-lightbox')) {
    mountLightboxInBody(dialog)
  }
}

function syncLightboxBodyLock() {
  const hasOpenLightbox = [...document.querySelectorAll('.js-product-lightbox')].some(
    (dialog) => dialog.open,
  )
  document.body.classList.toggle(LIGHTBOX_BODY_CLASS, hasOpenLightbox)
}

function pauseLightboxMedia(root) {
  if (!root) return
  resetLightboxZoom(root)
  for (const video of root.querySelectorAll('video')) {
    video.pause()
  }
  for (const iframe of root.querySelectorAll('iframe')) {
    iframe.src = iframe.src
  }
}

function getLightboxZoomLabel(zoomed) {
  const strings = window.productStrings || {}
  if (zoomed) return strings.zoomOut || 'Zoom out'
  return strings.zoomIn || 'Zoom in'
}

function getLightboxZoomImage(frame) {
  return frame?.querySelector('.js-gallery-lightbox-slide-img') ?? null
}

function setLightboxZoomTransform(frame) {
  const img = getLightboxZoomImage(frame)
  if (!img) return

  const scale = Number(frame.dataset.zoomScale) || 1
  const x = Number(frame.dataset.zoomX) || 0
  const y = Number(frame.dataset.zoomY) || 0

  if (scale <= 1) {
    img.style.transform = ''
    frame.classList.remove(LIGHTBOX_ZOOM_CLASS)
    frame.setAttribute('aria-label', getLightboxZoomLabel(false))
    return
  }

  frame.classList.add(LIGHTBOX_ZOOM_CLASS)
  frame.setAttribute('aria-label', getLightboxZoomLabel(true))
  img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`
}

function resetLightboxZoom(root) {
  if (!root) return

  const frames = root.matches?.('.js-gallery-lightbox-zoom')
    ? [root]
    : [...root.querySelectorAll('.js-gallery-lightbox-zoom')]

  for (const frame of frames) {
    frame.dataset.zoomScale = '1'
    frame.dataset.zoomX = '0'
    frame.dataset.zoomY = '0'
    frame.dataset.skipClick = ''
    frame.classList.remove(LIGHTBOX_ZOOM_DRAG_CLASS)
    setLightboxZoomTransform(frame)
  }
}

function zoomLightboxImageAtPoint(frame, event) {
  const img = getLightboxZoomImage(frame)
  if (!img) return

  const rect = img.getBoundingClientRect()
  const offsetX = event.clientX - (rect.left + rect.width / 2)
  const offsetY = event.clientY - (rect.top + rect.height / 2)
  const scale = LIGHTBOX_ZOOM_SCALE

  frame.dataset.zoomScale = String(scale)
  frame.dataset.zoomX = String(-offsetX * (scale - 1))
  frame.dataset.zoomY = String(-offsetY * (scale - 1))
  setLightboxZoomTransform(frame)
}

function initLightboxZoom(dialog) {
  const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
  if (!carousel || carousel.dataset.lightboxZoomInit === 'true') return

  let drag = null

  carousel.addEventListener(
    'pointerdown',
    (event) => {
      const frame = event.target.closest('.js-gallery-lightbox-zoom')
      if (!frame?.classList.contains(LIGHTBOX_ZOOM_CLASS)) return

      event.stopPropagation()
      drag = {
        frame,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: Number(frame.dataset.zoomX) || 0,
        originY: Number(frame.dataset.zoomY) || 0,
        moved: false,
      }
      frame.classList.add(LIGHTBOX_ZOOM_DRAG_CLASS)
    },
    true,
  )

  carousel.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return

    event.preventDefault()
    event.stopPropagation()

    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (Math.hypot(dx, dy) > LIGHTBOX_ZOOM_DRAG_THRESHOLD) drag.moved = true

    drag.frame.dataset.zoomX = String(drag.originX + dx)
    drag.frame.dataset.zoomY = String(drag.originY + dy)
    setLightboxZoomTransform(drag.frame)
  })

  const endDrag = (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return
    drag.frame.classList.remove(LIGHTBOX_ZOOM_DRAG_CLASS)
    drag.frame.dataset.skipClick = drag.moved ? 'true' : ''
    drag = null
  }

  carousel.addEventListener('pointerup', endDrag)
  carousel.addEventListener('pointercancel', endDrag)

  carousel.addEventListener('click', (event) => {
    const frame = event.target.closest('.js-gallery-lightbox-zoom')
    if (!frame) return
    if (event.target.closest('button, a, video, iframe')) return

    if (frame.dataset.skipClick === 'true') {
      frame.dataset.skipClick = ''
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (frame.classList.contains(LIGHTBOX_ZOOM_CLASS)) {
      resetLightboxZoom(frame)
      return
    }

    zoomLightboxImageAtPoint(frame, event)
  })

  for (const frame of carousel.querySelectorAll('.js-gallery-lightbox-zoom')) {
    frame.setAttribute('aria-label', getLightboxZoomLabel(false))
  }

  carousel.dataset.lightboxZoomInit = 'true'
}

function shouldUseLightboxFade(carousel) {
  return (
    carousel instanceof HTMLElement &&
    carousel.hasAttribute('data-fade-slides') &&
    window.matchMedia(LIGHTBOX_FADE_MEDIA_QUERY).matches
  )
}

function isLightboxSlideClone(item) {
  return item instanceof HTMLElement && item.hasAttribute('data-clone')
}

function getLightboxSlideItems(carousel) {
  if (!carousel) return []

  return [...carousel.querySelectorAll('sl-carousel-item')].filter(
    (item) => !isLightboxSlideClone(item),
  )
}

function syncLightboxSlideInteractivity(carousel, activeIndex) {
  if (!carousel) return

  for (const [index, item] of getLightboxSlideItems(carousel).entries()) {
    const active = index === activeIndex
    item.inert = !active
    if (active) {
      item.removeAttribute('aria-hidden')
    } else {
      item.setAttribute('aria-hidden', 'true')
    }
  }
}

function playLightboxVideos(slide) {
  if (!slide) return

  for (const video of slide.querySelectorAll('video')) {
    const playAttempt = video.play()
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => { })
    }
  }
}

function pauseProductGalleryVideos() {
  for (const video of document.querySelectorAll('.js-bento-cell video')) {
    video.pause()
  }
}

function resumeProductGalleryVideos() {
  for (const video of document.querySelectorAll('.js-bento-cell video')) {
    if (!video.hasAttribute('autoplay')) continue
    const playAttempt = video.play()
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => { })
    }
  }
}

function getLightboxDialog(carousel) {
  return carousel?.closest('.js-product-lightbox') ?? null
}

function getLightboxSlideIndexForMediaId(carousel, mediaId) {
  if (!carousel || mediaId == null || mediaId === '') return -1

  const targetId = String(mediaId)
  for (const [index, item] of getLightboxSlideItems(carousel).entries()) {
    if (item.dataset.mediaId === targetId) return index
  }

  return -1
}

function getLightboxActiveIndex(carousel) {
  if (shouldUseLightboxFade(carousel)) {
    const stored = Number(carousel.dataset.lightboxActiveIndex)
    if (!Number.isNaN(stored)) return stored
  }

  return carousel.activeSlide ?? 0
}

function resolveLightboxOpenIndex(dialog, { index, mediaId }) {
  const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
  if (!carousel) return index

  const resolved = getLightboxSlideIndexForMediaId(carousel, mediaId)
  if (resolved >= 0) return resolved

  return index
}

function getLightboxSlideBehavior(carousel) {
  return shouldUseLightboxFade(carousel) ? 'instant' : 'auto'
}

function syncLightboxSlideFade(carousel, activeIndex) {
  if (!carousel) return

  for (const [index, item] of getLightboxSlideItems(carousel).entries()) {
    item.classList.toggle(LIGHTBOX_SLIDE_ACTIVE_CLASS, index === activeIndex)
  }

  syncLightboxSlideInteractivity(carousel, activeIndex)
}

function syncLightboxFadeMode(carousel) {
  if (!carousel) return

  const fade = shouldUseLightboxFade(carousel)
  carousel.classList.toggle(LIGHTBOX_FADE_READY_CLASS, fade)
  carousel.mouseDragging = !fade

  if (fade) {
    syncLightboxSlideFade(carousel, getLightboxActiveIndex(carousel))
    return
  }

  for (const item of getLightboxSlideItems(carousel)) {
    item.classList.remove(LIGHTBOX_SLIDE_ACTIVE_CLASS)
  }
}

function syncLightboxSlideState(carousel, index) {
  const dialog = getLightboxDialog(carousel)
  if (!dialog) return

  syncLightboxThumbnails(dialog, index)
  pauseInactiveSlides(dialog, index)
}

function goToLightboxSlide(carousel, index) {
  if (shouldUseLightboxFade(carousel)) {
    carousel.dataset.lightboxActiveIndex = String(index)
    syncLightboxSlideFade(carousel, index)
    syncLightboxSlideState(carousel, index)
    return
  }

  carousel.goToSlide(index, getLightboxSlideBehavior(carousel))
}

function navigateLightboxSlide(carousel, delta) {
  const items = getLightboxSlideItems(carousel)
  const count = items.length
  if (count <= 1) return

  const current = getLightboxActiveIndex(carousel)
  let next = current + delta

  if (carousel.loop || shouldUseLightboxFade(carousel)) {
    next = (next + count) % count
  } else {
    next = Math.max(0, Math.min(count - 1, next))
  }

  if (next === current) return

  goToLightboxSlide(carousel, next)
}

function isLightboxNavButton(node) {
  return (
    node instanceof Element &&
    typeof node.getAttribute === 'function' &&
    node.getAttribute('part')?.includes('navigation-button')
  )
}

function handleLightboxCarouselNavClick(event) {
  const carousel = event.currentTarget
  if (!shouldUseLightboxFade(carousel)) return

  const navButton = event.composedPath().find(isLightboxNavButton)
  if (!navButton) return

  event.preventDefault()
  event.stopPropagation()

  const part = navButton.getAttribute('part') || ''
  navigateLightboxSlide(carousel, part.includes('next') ? 1 : -1)
}

const LIGHTBOX_SLIDE_IMG_LOADED_CLASS = 'is-lightbox-slide-img-loaded'

function markLightboxSlideImageLoaded(img) {
  img.classList.add(LIGHTBOX_SLIDE_IMG_LOADED_CLASS)
}

function loadLightboxSlideImage(slide) {
  if (!slide) return

  const imgs = slide.querySelectorAll('.js-gallery-lightbox-slide-img[data-lightbox-src]')
  for (const img of imgs) {
    if (img.dataset.lightboxLoaded === 'true') continue

    const src = img.dataset.lightboxSrc
    if (!src) continue

    img.classList.remove(LIGHTBOX_SLIDE_IMG_LOADED_CLASS)

    const finishLoad = () => {
      markLightboxSlideImageLoaded(img)
      img.removeEventListener('load', finishLoad)
      img.removeEventListener('error', finishLoad)
    }

    img.addEventListener('load', finishLoad)
    img.addEventListener('error', finishLoad)
    img.src = src
    img.dataset.lightboxLoaded = 'true'

    if (img.complete) {
      finishLoad()
    }
  }
}

function loadLightboxSlideImages(dialog, activeIndex) {
  const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
  const items = getLightboxSlideItems(carousel)

  loadLightboxSlideImage(items[activeIndex])
  loadLightboxSlideImage(items[activeIndex - 1])
  loadLightboxSlideImage(items[activeIndex + 1])
}

function loadSlideModel(slide) {
  const model = slide?.querySelector('product-model')
  if (model && typeof model.loadContent === 'function') {
    requestAnimationFrame(() => model.loadContent())
  }
}

function syncLightboxThumbnails(dialog, slideIndex) {
  const thumbs = dialog.querySelectorAll('.js-gallery-lightbox-thumb')
  for (const [index, thumb] of thumbs.entries()) {
    const active = index === slideIndex
    thumb.classList.toggle('active', active)
    if (active) {
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }
}

function pauseInactiveSlides(dialog, activeIndex) {
  const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
  const items = getLightboxSlideItems(carousel)

  syncLightboxSlideInteractivity(carousel, activeIndex)

  for (const [index, item] of items.entries()) {
    if (index === activeIndex) {
      loadSlideModel(item)
      playLightboxVideos(item)
      continue
    }
    resetLightboxZoom(item)
    for (const video of item.querySelectorAll('video')) {
      video.pause()
    }
  }

  loadLightboxSlideImages(dialog, activeIndex)
}

function initLightboxDialog(dialog) {
  if (dialog.dataset.lightboxInit === 'true') return

  const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
  const scroller = dialog.querySelector('.js-gallery-lightbox-thumbnails')
  if (!carousel) return

  syncLightboxFadeMode(carousel)
  initLightboxZoom(dialog)

  carousel.addEventListener('sl-slide-change', (event) => {
    if (shouldUseLightboxFade(carousel)) return

    const slideIndex = event.detail.index
    syncLightboxThumbnails(dialog, slideIndex)
    pauseInactiveSlides(dialog, slideIndex)
  })

  carousel.addEventListener('click', handleLightboxCarouselNavClick, true)

  if (scroller) {
    scroller.addEventListener('click', (event) => {
      const thumb = event.target.closest('.js-gallery-lightbox-thumb')
      if (!thumb) return
      const index = Number(thumb.dataset.mediaIndex)
      if (!Number.isNaN(index)) {
        goToLightboxSlide(carousel, index)
      }
    })
  }

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      if (shouldUseLightboxFade(carousel)) {
        navigateLightboxSlide(carousel, -1)
      } else {
        carousel.previous()
      }
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      if (shouldUseLightboxFade(carousel)) {
        navigateLightboxSlide(carousel, 1)
      } else {
        carousel.next()
      }
    }
  })

  dialog.dataset.lightboxInit = 'true'
}

async function openGalleryLightbox(dialog, { index, mediaId }) {
  mountLightboxInBody(dialog)
  initLightboxDialog(dialog)

  if (typeof dialog.show === 'function') {
    await dialog.show()
  }

  syncLightboxBodyLock()
  pauseProductGalleryVideos()

  const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
  if (!carousel) return

  const slideIndex = resolveLightboxOpenIndex(dialog, { index, mediaId })

  carousel.dataset.lightboxActiveIndex = String(slideIndex)
  syncLightboxFadeMode(carousel)
  goToLightboxSlide(carousel, slideIndex)

  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })

  goToLightboxSlide(carousel, slideIndex)
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('.js-gallery-lightbox-open')
  if (!opener || opener.disabled) return

  const dialogId = opener.dataset.previewDialog
  const dialog = dialogId ? document.getElementById(dialogId) : null
  const index = Number(opener.dataset.mediaIndex)
  const mediaId = opener.dataset.mediaId
  if (!dialog || Number.isNaN(index)) return

  event.preventDefault()
  openGalleryLightbox(dialog, { index, mediaId })
})

function updateGalleryLightbox(content) {
  if (!content?.id) return

  const existing = document.getElementById(content.id)
  if (!existing) return

  if (existing.open && typeof existing.hide === 'function') {
    existing.hide()
  }

  pauseLightboxMedia(existing)
  existing.replaceWith(content)
  mountLightboxInBody(content)
}

document.addEventListener('nova:product:render-gallery-lightbox', (event) => {
  updateGalleryLightbox(event.detail.html)
})

document.addEventListener(
  'sl-after-show',
  (event) => {
    if (!isProductGalleryLightbox(event.target)) return
    mountLightboxInBody(event.target)
    syncLightboxBodyLock()
  },
  true,
)

document.addEventListener(
  'sl-after-hide',
  (event) => {
    if (!isProductGalleryLightbox(event.target)) return
    pauseLightboxMedia(event.target)
    resumeProductGalleryVideos()
    syncLightboxBodyLock()
  },
  true,
)

window
  .matchMedia(LIGHTBOX_FADE_MEDIA_QUERY)
  .addEventListener('change', () => {
    for (const dialog of document.querySelectorAll('.js-product-lightbox')) {
      const carousel = dialog.querySelector('.js-gallery-lightbox-carousel')
      if (!carousel) continue
      syncLightboxFadeMode(carousel)
    }
  })

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAllLightboxes, { once: true })
} else {
  mountAllLightboxes()
}
